import React, { useEffect, useMemo } from "react";
import { useProgression } from "../context/ProgressionContext";
import { allThemes } from "../modules/themes";
import { allMicroCourses } from "../modules/academy/models";
import { useParcours } from "../context/ParcoursContext";
import { allParcours } from "../modules/academy/parcours";
import { DashboardSection, DashboardCard, DashboardLink } from "../components/dashboard";
import { Link } from "react-router-dom";
import { MixmindUniverse, useUniverse } from "../context/UniverseContext";

export default function AcademyDashboard() {
  const { progress, isGuest } = useProgression();
  const { parcoursProgress, getNextStep } = useParcours();
  const { setUniverse } = useUniverse();

  useEffect(() => {
    setUniverse(MixmindUniverse.Academy);
  }, [setUniverse]);

  const startedThemes = progress?.startedThemes || [];
  const themeProgress = progress?.themeProgress || {};

  const recentMicroCourses = useMemo(() => {
    const ids = progress?.completedMicroCourses || [];
    const lastIds = ids.slice(-3).reverse();
    return lastIds
      .map((id) => allMicroCourses.find((mc) => mc.id === id))
      .filter((mc): mc is typeof allMicroCourses[number] => Boolean(mc));
  }, [progress]);

  const quizCount = progress ? Object.keys(progress.quizScores || {}).length : 0;
  const parcoursCards = useMemo(() => {
    return Object.entries(parcoursProgress || {})
      .filter(([, state]) => state?.started)
      .map(([parcoursId, state]) => {
        const parcours = allParcours.find((p) => p.id === parcoursId);
        if (!parcours) return null;
        const percent = parcours.steps.length
          ? Math.round((state.completedSteps.length / parcours.steps.length) * 100)
          : 0;
        const next = getNextStep(parcoursId, parcours.steps);
        return { parcours, percent, next };
      })
      .filter(Boolean) as { parcours: (typeof allParcours)[number]; percent: number; next: any }[];
  }, [parcoursProgress, getNextStep]);

  return (
    <div className="max-w-5xl mx-auto py-8 px-4 space-y-8">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold mb-4 text-slate-900 dark:text-slate-50">Dashboard Academy</h1>
        {isGuest && (
          <p className="text-sm text-slate-700 dark:text-slate-300">
            Mode invite : connectez-vous pour sauvegarder votre progression.
          </p>
        )}
        <DashboardLink to="/academy" className="text-sm">
          Voir Academy
        </DashboardLink>
      </header>

      <DashboardSection title="Mes thèmes en cours">
        {startedThemes.length === 0 ? (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-700 dark:text-slate-300">Aucun thème commencé pour l’instant.</p>
            <DashboardLink to="/academy/themes">Découvrir les thèmes</DashboardLink>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid gap-4 md:grid-cols-2">
              {startedThemes.map((themeId) => {
                const theme = allThemes.find((t) => t.id === themeId);
                if (!theme) return null;
                const percent = themeProgress[themeId] ?? 0;
                return (
                  <div
                    key={themeId}
                    className="mm-surface p-4 space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="text-base font-semibold text-slate-900 dark:text-slate-50">{theme.title}</h3>
                      <span className="text-xs text-slate-500 dark:text-slate-400">{percent}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
                      <div
                        className="h-full bg-indigo-500"
                        style={{ width: `${Math.min(Math.max(percent, 0), 100)}%` }}
                      />
                    </div>
                    <Link
                      to={`/theme/${themeId}`}
                      className="text-sm text-indigo-600 hover:text-indigo-700 dark:text-indigo-300 dark:hover:text-indigo-200 underline-offset-2 hover:underline"
                    >
                      Continuer
                    </Link>
                  </div>
                );
              })}
            </div>
            <DashboardLink to="/academy/themes">Découvrir les thèmes</DashboardLink>
          </div>
        )}
      </DashboardSection>

      <DashboardSection title="Mes parcours en cours">
        {parcoursCards.length === 0 ? (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-700 dark:text-slate-300">Tu n’as pas encore de parcours en cours.</p>
            <DashboardLink to="/academy/parcours">Voir les parcours</DashboardLink>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid gap-4 md:grid-cols-2">
              {parcoursCards.map(({ parcours, percent, next }) => (
                <div
                  key={parcours.id}
                  className="mm-surface p-4 space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-slate-900 dark:text-slate-50">{parcours.title}</h3>
                    <span className="text-xs text-slate-500 dark:text-slate-400">{percent}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
                    <div
                      className="h-full bg-indigo-500"
                      style={{ width: `${Math.min(Math.max(percent, 0), 100)}%` }}
                    />
                  </div>
                  {next && (
                    <p className="text-xs text-slate-500 dark:text-slate-400">Prochaine étape : {next.title || next.type}</p>
                  )}
                  <DashboardLink to={`/parcours/${parcours.id}`}>Continuer le parcours</DashboardLink>
                </div>
              ))}
            </div>
            <DashboardLink to="/academy/parcours">Voir les parcours</DashboardLink>
          </div>
        )}
      </DashboardSection>

      <DashboardSection title="Activité récente">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="mm-surface p-4">
            <DashboardCard title="Derniers micro-cours terminés" isEmpty={recentMicroCourses.length === 0} emptyText="Pas encore de micro-cours terminés.">
              <ul className="text-sm text-slate-700 dark:text-slate-200 list-disc list-inside space-y-1">
                {recentMicroCourses.map((mc) => (
                  <li key={mc.id}>{mc.title}</li>
                ))}
              </ul>
            </DashboardCard>
          </div>
          <div className="mm-surface p-4">
            <DashboardCard title="Quiz complétés" isEmpty={quizCount === 0} emptyText="Aucun quiz terminé pour l’instant.">
              <p className="text-sm text-slate-700 dark:text-slate-200">
                {quizCount} quiz complété(s). Prochain objectif : améliorer tes scores.
              </p>
            </DashboardCard>
          </div>
        </div>
      </DashboardSection>

      <DashboardSection title="Badges & jalons (à venir)">
        <p className="text-sm text-slate-700 dark:text-slate-300">
          Les badges seront disponibles prochainement. Tu pourras suivre tes jalons et objectifs personnalisés ici.
        </p>
      </DashboardSection>

      <DashboardSection title="Recommandations">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <p className="text-sm text-slate-700 dark:text-slate-300">
            Explore de nouveaux thèmes ou termine ceux en cours pour augmenter ta progression.
          </p>
          <DashboardLink to="/academy/themes">Découvrir les thèmes</DashboardLink>
        </div>
      </DashboardSection>
    </div>
  );
}



